import type { QueryClient } from '@tanstack/react-query';

import type { ActivityEventDto } from './dto';
import { qk } from './query-keys';

/**
 * جدول تحويل حدث الأثر إلى invalidations (§1.3 وقرار تحكيم D28) — **جدول ثابت لا
 * اجتهاد في المكوّنات**: كل حدث وارد يحمل `verb + entity_type + entity_id +
 * project_id` (الأخير nullable ومشتق من الكيان — قرار D5)، والقاعدة المعلنة هي:
 * نقرأ `project_id` فعليًا، وعندما يكون `null` نسقط إلى إبطال عريض `['projects']`.
 *
 * الـ invalidations **idempotent بطبيعتها**، فتكرار الحدث الذي يضمنه عقد D49 لا
 * يُنتج أثرًا مزدوجًا — ولذلك لا إزالة تكرار هنا. إزالة التكرار لازمة في مكان واحد
 * فقط: إدراج الحدث في تغذيةٍ تراكمية — ولا تغذية كذلك اليوم بعد حذف شاشة النشاط.
 *
 * البادئات العريضة (`['tasks']`, `['projects']`, `['captures']`) مقصودة: هي نفس
 * النطاقات التي تستعملها طفرات المشاريع (`invalidateProjectScope`) — فالحدث الوارد
 * والطفرة المحلية يتفقان على نطاق واحد ولا ينحرف أحدهما عن الآخر.
 */

/**
 * كل عائلة تُعلن شيئين مفصولين عمدًا:
 *
 *   • `scopes` — البادئات **العريضة** التي تكفي وحدها لإبطال كل ما يخص العائلة.
 *   • `scoped` — ما يعتمد على الحدث نفسه (`project_id`)، فلا معنى له في إبطالٍ عريض.
 *
 * والفصل ليس تنظيمًا: `refreshAfterUndo` في شاشة النشاط كان يحمل **قائمة يدوية ثانية**
 * لنفس العائلات، وتعليقُه يقول إنها «نفس العائلات التي يعرفها جدول إبطال SSE» — وقد
 * انحرفت فعلًا: نقصتها `milestones` و`library` و`decisions` وحالة مزامنة
 * هيرمز، وكلها كيانات **قابلة للتراجع** بنصّ `UNDOABLE_ENTITIES`. فصار الجدول مصدرًا
 * واحدًا يقرأ منه الاثنان بدل نسختين تتباعدان بصمت.
 */
interface FamilyRule {
  readonly scopes: readonly (readonly string[])[];
  readonly scoped?: (qc: QueryClient, event: ActivityEventDto) => void;
}

const INVALIDATION_MAP: Readonly<Record<string, FamilyRule>> = {
  task: { scopes: [['tasks'], qk.agendaToday()] },
  project: { scopes: [['projects']] },
  capture: { scopes: [['captures'], qk.agendaToday()] },
  entry: {
    scopes: [['entries']],
    scoped: (qc, event) => {
      if (event.project_id === null) {
        void qc.invalidateQueries({ queryKey: ['projects'] });
        return;
      }
      void qc.invalidateQueries({ queryKey: qk.projectStatus(event.project_id) });
      void qc.invalidateQueries({ queryKey: qk.projectTimeline(event.project_id) });
    },
  },
  /*
   * `['decisions']` بادئة عريضة صارت مُعلَنة هنا وكانت غائبة: الحدث كان يُبطل قرارات
   * **مشروعه** وحدها. والتوسعة مقصودة ورخيصة — استعلامات المشاريع الأخرى غير نشطة
   * فلا تُعاد حتى تُفتح شاشتها — وبها يصير التراجع عن قرار محسوسًا في الشاشة.
   */
  decision: {
    scopes: [['decisions']],
    scoped: (qc, event) => {
      if (event.project_id === null) {
        void qc.invalidateQueries({ queryKey: ['projects'] });
        return;
      }
      void qc.invalidateQueries({ queryKey: qk.project(event.project_id) });
    },
  },
  /**
   * محادثة القسم (ج1) — **بلا `scoped`**.
   *
   * الحدث يحمل `project_id` لا `axis_id`، فإبطالُ نطاق المشروع منه يُبطل
   * محادثات أقسامه كلها بينما تغيّر واحدة. والبادئة العريضة أرخص من دقّةٍ
   * كاذبة: استعلامات الأقسام غير المفتوحة ليست نشطة فلا تُعاد أصلًا.
   */
  conversation: { scopes: [['conversation']] },
  reminder: { scopes: [['reminders']] },
  /* الأفكار كيان شخصي عالميّ: بادئة خاصة بلا `scoped` — لا `project_id`
     له أصلًا، فإبطالُ نطاق مشروعٍ هنا يكون كذبًا لا اقتصادًا. */
  idea: { scopes: [['ideas']] },
  /* والمفكرة مثلها: كيانان شخصيان عالميّان بلا `project_id`. */
  journal_entry: { scopes: [['journal', 'entries']] },
  quick_note: { scopes: [['journal', 'notes']] },
  /* المتابعة اليومية: كيانٌ شخصيّ بلا `project_id` — والسطر من البوت يُبطل ملاحظاته أيضًا. */
  daily_record: { scopes: [qk.dailyScope(), qk.quickNotes()] },
  publishing_post: { scopes: [['publishing']] },
  archive_item: { scopes: [['archive']] },
  news_item: { scopes: [['news']] },
  milestone: {
    scopes: [['milestones']],
    // `milestone.completed` يُنعش `last_activity_at` ويُسقط الركود (قرار D53) —
    // فبطاقة المشروع و«وين وصلنا؟» تتغيران مع الحدث لا بعد إعادة فتح الشاشة.
    scoped: (qc, event) => {
      if (event.project_id === null) {
        void qc.invalidateQueries({ queryKey: ['projects'] });
        return;
      }
      void qc.invalidateQueries({ queryKey: qk.project(event.project_id) });
      void qc.invalidateQueries({ queryKey: qk.projectStatus(event.project_id) });
    },
  },
  // المهارة والشخصية تحت بادئة `['library']` واحدة، وأي كتابة فيهما قد تقلب
  // `is_out_of_sync` — فحالة المزامنة تُبطل معهما لا على حدة.
  skill: { scopes: [['library'], qk.hermesSyncStatus()] },
  persona: { scopes: [['library'], qk.hermesSyncStatus()] },
  /** `hermes.synced` بلا مشروع (`project_id: null`) — يختم صفوف المكتبة كلها. */
  hermes: { scopes: [['library'], qk.hermesSyncStatus()] },
  venture: { scopes: [qk.ventures()] },
  /**
   * **كانت غائبة كليًّا** حتى المرحلة ب1: أحداث المحاور الثلاثة
   * (`axis.created` · `axis.updated` · `axis.status_changed`) لم تكن تُبطل شيئًا،
   * فلم يظهر أثرها لأن الويب لم يكن يعرض المحاور أصلًا. ومع وجه «الأقسام» صار
   * الغياب عطبًا حيًّا: قسمٌ يُنشئه أمرٌ من MCP أو تيليجرام لا يظهر حتى
   * إعادة تحميل الصفحة يدويًا — الشاشة تعمل مرة واحدة ثم تكذب.
   *
   * و`status_changed` يلمس `last_activity_at` (فعل ذو معنى)، فتُبطل معه بطاقة
   * المشروع وتقريره لا قائمة الأقسام وحدها.
   */
  project_axis: {
    scopes: [['projects', 'axes']],
    scoped: (qc, event) => {
      if (event.project_id === null) {
        void qc.invalidateQueries({ queryKey: ['projects'] });
        return;
      }
      void qc.invalidateQueries({ queryKey: qk.project(event.project_id) });
      void qc.invalidateQueries({ queryKey: qk.projectStatus(event.project_id) });
    },
  },
  // ذاكرة المشروع تُكتب أيضًا من أزرار تيليجرام؛ فتح الوجه في الويب يجب أن يلتقط
  // الاعتماد والمصدر الواردين فورًا، لا بعد إعادة تحميل الصفحة يدويًا.
  project_memory: { scopes: [['projects', 'memory']] },
  source_reference: { scopes: [['projects', 'sources']] },
  commerce_store: { scopes: [qk.commerce()] },
  commerce_daily_metric: { scopes: [qk.commerce()] },
  commerce_daily_report: { scopes: [qk.commerce()] },
  /* العُهد تُكتب أساسًا من بوت مستقل؛ كل حدث ينعش الرصيد والجهات والحركات معًا. */
  agent_job: { scopes: [qk.agentJobs()] },
};

/**
 * كل البادئات العريضة التي يعرفها الجدول — بلا تكرار.
 *
 * يستعملها التراجع من شاشة النشاط: أثره أوسع من صف واحد (يُحيي محذوفًا ناعمًا، أو
 * يعكس تطبيق مقترح أنشأ عدة كيانات)، والكيان المتأثر قد يكون من أي عائلة يسمح بها
 * `UNDOABLE_ENTITIES` — فالإبطال بحدود الجدول كلّه لا باجتهاد.
 */
export const BROAD_INVALIDATION_SCOPES: readonly (readonly string[])[] = Object.freeze(
  [...new Map(
    Object.values(INVALIDATION_MAP)
      .flatMap((rule) => rule.scopes)
      .map((scope) => [scope.join(' '), scope]),
  ).values()],
);

export function invalidateForActivityEvent(qc: QueryClient, event: ActivityEventDto): void {
  const rule = INVALIDATION_MAP[event.entity_type];
  if (rule !== undefined) {
    for (const scope of rule.scopes) void qc.invalidateQueries({ queryKey: scope });
    rule.scoped?.(qc, event);
  }
}
